/**
 * Live route strip: arrived / in transit / next / later, derived from the
 * same timeline the monitor already publishes. The widget only paints this.
 */
import {
  Mission,
  MissionDeviceState,
  MissionProgress,
  MissionRouteEvent,
  MissionRouteEventType,
  MissionTimelineStop,
} from '../../services/missionApi';
import { isGenericWaypointLabel, sequentialWaypointLabel } from '../routePlanner/io/waypointLabel';
import { formatAgeMinutes, formatMeters, formatSpan } from './format';

const THREAT_TYPES = new Set<MissionRouteEventType>([
  'watchlist_on_route',
  'cotraveler_confirmed',
  'cotraveler_sustained',
  'uas_intercept',
  'surveillance_ahead',
  'corridor_threat',
]);

const ON_TIME_S = 90;
const AT_STOP_M = 80;
const PLACE_NAME_MAX = 48;

export type StopPhase = 'reached' | 'next' | 'later';

export interface RouteStopView {
  id: number | string;
  index: number;
  label: string;
  phase: StopPhase;
  latitude?: number | null;
  longitude?: number | null;
  note?: string;
  clock: string | null;
  plannedClock: string | null;
  vsPlan: string | null;
  late: boolean;
  detail: string;
  threats: number;
  coords: string | null;
}

export interface TransitView {
  leg: number;
  of: number;
  stopped: boolean;
  live: number;
  total: number;
  spread: string;
}

export interface RouteTimelineView {
  km: string | null;
  stopCount: number;
  profile: string | null;
  recalcAt: string | null;
  stops: RouteStopView[];
  nextIndex: number;
  transit: TransitView | null;
}

export function parseIso(value?: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function formatCompactClock(iso?: string | null): string | null {
  const ms = parseIso(iso);
  if (ms == null) return null;
  return new Date(ms).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatRecalcAge(iso: string | null | undefined, nowMs: number): string | null {
  const then = parseIso(iso);
  if (then == null) return null;
  const seconds = Math.max(0, Math.round((nowMs - then) / 1000));
  if (seconds < 60) return `recalculated ${seconds}s ago`;
  return `recalculated ${formatAgeMinutes(seconds / 60)}`;
}

export function plannedArrivalIso(args: {
  startedAt?: string | null;
  plannedDurationS?: number | null;
  distanceAlongM?: number | null;
  totalDistanceM?: number | null;
}): string | null {
  const start = parseIso(args.startedAt);
  const duration = args.plannedDurationS;
  const total = args.totalDistanceM;
  if (start == null || !duration || duration <= 0 || !total || total <= 0) return null;
  const along = Math.max(0, args.distanceAlongM ?? 0);
  const ratio = Math.min(1, along / total);
  return new Date(start + duration * ratio * 1000).toISOString();
}

export function deltaSeconds(actualIso?: string | null, plannedIso?: string | null): number | null {
  const actual = parseIso(actualIso);
  const planned = parseIso(plannedIso);
  if (actual == null || planned == null) return null;
  return (actual - planned) / 1000;
}

export function formatVsPlan(deltaS: number | null): string | null {
  if (deltaS == null) return null;
  if (Math.abs(deltaS) < ON_TIME_S) return 'on time';
  const span = formatSpan(Math.abs(deltaS));
  return deltaS > 0 ? `+${span} vs plan` : `${span} ahead`;
}

export function convoySpreadM(devices: MissionDeviceState[]): number | null {
  const along = devices
    .filter((d) => d.signal_state !== 'lost' && d.distance_along_m != null)
    .map((d) => d.distance_along_m as number);
  if (along.length < 2) return null;
  return Math.max(...along) - Math.min(...along);
}

export function reportingCount(devices: MissionDeviceState[]): { live: number; total: number } {
  const total = devices.length;
  const live = devices.filter((d) => d.signal_state === 'live').length;
  return { live, total };
}

function haversineM(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const r = 6371000;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
  const lat1 = (a.latitude * Math.PI) / 180;
  const lat2 = (b.latitude * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(x)));
}

export function threatsTowardStop(
  stopIndex: number,
  stops: Array<{ latitude?: number | null; longitude?: number | null; reached_at?: string | null }>,
  events: MissionRouteEvent[],
): number {
  const open = events.filter((e) => THREAT_TYPES.has(e.event_type) && !e.acknowledged_at);
  if (open.length === 0) return 0;
  const nextOpen = stops.findIndex((s) => !s.reached_at);
  let count = 0;
  for (const event of open) {
    if (event.latitude == null || event.longitude == null) {
      if (stopIndex === nextOpen) count += 1;
      continue;
    }
    let best = -1;
    let bestM = Infinity;
    stops.forEach((stop, i) => {
      if (stop.reached_at || stop.latitude == null || stop.longitude == null) return;
      const d = haversineM(
        { latitude: event.latitude!, longitude: event.longitude! },
        { latitude: stop.latitude, longitude: stop.longitude },
      );
      if (d < bestM) {
        bestM = d;
        best = i;
      }
    });
    if (best === stopIndex) count += 1;
  }
  return count;
}

/** Generic "Waypoint 1" plus a short note is how a named place was stored. */
export function stopTitle(
  label: string | null | undefined,
  notes: string | null | undefined,
  index: number,
): { label: string; note: string } {
  const note = (notes || '').trim();
  if (isGenericWaypointLabel(label) && note && !note.includes('\n') && note.length <= PLACE_NAME_MAX) {
    return { label: note, note: '' };
  }
  return { label: sequentialWaypointLabel(label, index), note };
}

export function lastRecalcAt(
  mission: Pick<Mission, 'updated_at'>,
  events: MissionRouteEvent[],
): string | null {
  const changed = events
    .filter((e) => e.event_type === 'route_changed')
    .map((e) => parseIso(e.detected_at))
    .filter((ms): ms is number => ms != null);
  if (changed.length > 0) return new Date(Math.max(...changed)).toISOString();
  return mission.updated_at || null;
}

function dwellDetail(
  stop: MissionTimelineStop,
  next: MissionTimelineStop | undefined,
  progress: MissionProgress | null | undefined,
  nowMs: number,
): string | null {
  const reached = parseIso(stop.reached_at);
  if (reached == null) return null;
  const atStop = progress
    && progress.stopped
    && !next?.reached_at
    && (
      stop.distance_along_m == null
      || Math.abs(progress.distance_along_m - stop.distance_along_m) <= AT_STOP_M
    );
  if (!atStop) return null;
  const span = formatSpan((nowMs - reached) / 1000);
  return span === '—' ? null : `dwell ${span}`;
}

export function buildRouteTimeline(args: {
  mission: Mission;
  timeline?: MissionTimelineStop[];
  progress?: MissionProgress | null;
  devices?: MissionDeviceState[];
  events?: MissionRouteEvent[];
  nowMs?: number;
  units?: 'km' | 'mi';
}): RouteTimelineView {
  const nowMs = args.nowMs ?? Date.now();
  const progress = args.progress || null;
  const devices = args.devices || [];
  const events = args.events || [];
  const units = args.units || 'mi';
  const stats = args.mission.route_stats;
  const totalM = stats?.distance ?? progress?.total_distance_m ?? null;
  const plannedDuration = stats?.duration ?? null;

  const raw: MissionTimelineStop[] = (args.timeline && args.timeline.length > 0)
    ? args.timeline
    : args.mission.waypoints.map((w, i) => ({
      id: w.id,
      seq: i,
      label: w.label,
      latitude: w.latitude,
      longitude: w.longitude,
      distance_along_m: w.distance_along_m,
      reached_at: w.reached_at,
      eta_at: null,
    }));

  const notesById = new Map(
    args.mission.waypoints.map((w) => [w.id, w.notes || ''] as const),
  );
  const notesByCoord = new Map(
    args.mission.waypoints.map((w) => [
      `${w.latitude.toFixed(5)},${w.longitude.toFixed(5)}`,
      w.notes || '',
    ]),
  );

  const live = args.mission.status === 'in_progress' || args.mission.status === 'baseline';
  const nextIndex = raw.findIndex((s) => !s.reached_at);
  const stops: RouteStopView[] = raw.map((stop, index) => {
    const phase: StopPhase = stop.reached_at
      ? 'reached'
      : live && index === nextIndex
        ? 'next'
        : 'later';
    const planned = plannedArrivalIso({
      startedAt: args.mission.started_at || progress?.started_at,
      plannedDurationS: plannedDuration,
      distanceAlongM: stop.distance_along_m,
      totalDistanceM: totalM,
    });
    const actual = stop.reached_at || stop.eta_at;
    const vs = formatVsPlan(deltaSeconds(actual, planned));
    const late = (deltaSeconds(actual, planned) || 0) >= ON_TIME_S;
    const dwell = phase === 'reached'
      ? dwellDetail(stop, raw[index + 1], progress, nowMs)
      : null;
    const threats = phase === 'later' || phase === 'next'
      ? threatsTowardStop(index, raw, events)
      : 0;
    const lat = stop.latitude;
    const lon = stop.longitude;
    const storedNote = (typeof stop.id === 'number' ? notesById.get(stop.id) : '')
      || (lat != null && lon != null
        ? notesByCoord.get(`${lat.toFixed(5)},${lon.toFixed(5)}`) || ''
        : '');
    const titled = stopTitle(stop.label, storedNote, index);
    const liveClock = formatCompactClock(stop.reached_at || stop.eta_at);
    const planClock = formatCompactClock(planned);
    const clock = stop.reached_at
      ? liveClock
      : liveClock && planClock && liveClock !== planClock && phase === 'later'
        ? `${planClock}–${liveClock}`
        : liveClock || planClock;
    const detailParts: string[] = [];
    if (phase === 'reached') {
      detailParts.push(vs === 'on time' ? 'arrived on time' : (vs || 'arrived'));
      if (dwell) detailParts.push(dwell);
    } else if (phase === 'next') {
      if (vs && vs !== 'on time') detailParts.push(vs);
      else if (vs === 'on time') detailParts.push('on time');
      detailParts.push('next stop');
    } else {
      if (!clock) detailParts.push('est.');
      if (threats > 0) {
        detailParts.push(`${threats} threat${threats === 1 ? '' : 's'} on leg`);
      }
    }
    return {
      id: stop.id ?? index,
      index,
      label: titled.label,
      phase,
      latitude: lat,
      longitude: lon,
      note: titled.note,
      clock,
      plannedClock: phase === 'next' && planClock && planClock !== liveClock ? planClock : null,
      vsPlan: vs,
      late,
      detail: detailParts.join(' · '),
      threats,
      coords: lat != null && lon != null ? `${lat.toFixed(4)}, ${lon.toFixed(4)}` : null,
    };
  });

  const reporting = reportingCount(devices);
  const spreadM = convoySpreadM(devices);
  const legs = Math.max(0, raw.length - 1);
  const showTransit = live && nextIndex >= 0 && legs > 0;

  return {
    km: totalM != null ? `${(totalM / 1000).toFixed(1)} km` : null,
    stopCount: raw.length,
    profile: args.mission.route_profile
      ? args.mission.route_profile.replace(/-/g, ' ')
      : null,
    recalcAt: lastRecalcAt(args.mission, events),
    stops,
    nextIndex,
    transit: showTransit && legs > 0
      ? {
        leg: Math.max(1, nextIndex < 0 ? legs : nextIndex),
        of: legs,
        stopped: !!progress?.stopped && (progress.remaining_m > 40),
        live: reporting.live,
        total: reporting.total,
        spread: spreadM != null ? formatMeters(spreadM, units) : '',
      }
      : null,
  };
}
